import { Activity, Database, GitBranch, Inbox, TriangleAlert, type LucideIcon } from 'lucide-react';

import type { AdminHealthCheck } from '@/admin/adminApi';
import type { BadgeTone } from '@/ui/kit';

/**
 * Ce que l'ecran de sante dit des verifications du serveur.
 *
 * Le serveur nomme chaque sonde par un identifiant technique (`database`,
 * `failed_transport`…), jamais par un libelle d'interface (contrat, §Exploitation).
 * Le dictionnaire est donc ici. **Une sonde inconnue n'est pas masquee** : elle
 * s'affiche sous son nom brut, pour qu'une sonde ajoutee cote serveur apparaisse
 * avant qu'on ait pense a la nommer.
 */

export type HealthStatus = AdminHealthCheck['status'];

interface Verdict {
  /** Mot de la tuile : court, il tient en grand corps dans une colonne etroite. */
  readonly label: string;
  /** Mot du badge global, a cote du titre de la page. */
  readonly overall: string;
  readonly tone: BadgeTone;
}

const VERDICTS: Readonly<Record<HealthStatus, Verdict>> = {
  ok: { label: 'Normal', overall: 'Opérationnel', tone: 'success' },
  degraded: { label: 'Dégradé', overall: 'Dégradé', tone: 'warning' },
  failed: { label: 'En échec', overall: 'En échec', tone: 'danger' },
};

/** Un statut que ce back-office ne connait pas se lit tel quel, en neutre. */
export const verdictOf = (status: string): Verdict =>
  (VERDICTS as Readonly<Record<string, Verdict | undefined>>)[status] ?? { label: status, overall: status, tone: 'neutral' };

interface Probe {
  readonly label: string;
  readonly icon: LucideIcon;
}

const PROBES: Readonly<Record<string, Probe | undefined>> = {
  database: { label: 'Base de données', icon: Database },
  migrations: { label: 'Migrations', icon: GitBranch },
  queue: { label: 'File d’attente', icon: Inbox },
  failed_transport: { label: 'Transport d’échec', icon: TriangleAlert },
};

export const probeOf = (name: string): Probe => PROBES[name] ?? { label: name, icon: Activity };

/** « répondu en 3 ms » ; `null` quand le serveur n'a pas mesure la sonde. */
export const latencySentence = (latencyMs: number | null): string | null =>
  latencyMs === null || !Number.isFinite(latencyMs)
    ? null
    : `répondu en ${latencyMs.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} ms`;
